/**
 * The model's P&L, laid out like the spreadsheet's "2025_2026 Model" sheet:
 * line items down, months across, calendar-year and horizon totals at the end.
 *
 * Views of the same statement. Plan is the sheet. Ramped plan is the sheet
 * scaled to the spend we actually have. Re-forecast replaces the months that
 * happened with what happened and projects the rest. Variance is re-forecast
 * against plan, line by line.
 */

import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui'
import {
  formatMoney, formatPercent, formatVariance, monthLabel, variance, yearTotals,
} from '../../lib/scienceModelView'

const LINES = [
  { label: 'Revenue', field: 'revenue', strong: true },
  { label: 'Refunds', field: r => -r.refunds },
  { label: 'Income', field: 'income', strong: true },
  { label: 'COGS — product', field: 'cogsProduct', indent: true },
  { label: 'COGS — other', field: 'cogsOther', indent: true },
  { label: 'COGS — shipping', field: 'cogsShipping', indent: true },
  { label: 'COGS — doctor visits', field: 'cogsDoctor', indent: true },
  { label: 'Total COGS', field: 'cogsTotal' },
  { label: 'Commissions', field: 'commissions' },
  { label: 'Gross profit', field: 'grossProfit', strong: true },
  { label: 'GP margin', field: 'gpMargin', ratio: ['grossProfit', 'income'] },
  { label: 'Advertising & marketing', field: r => r.expenses.advertising, indent: true },
  { label: 'Agency fees', field: r => r.expenses.agency, indent: true },
  { label: 'Management fees', field: r => r.expenses.management, indent: true },
  { label: 'Merchant fees', field: r => r.expenses.merchant, indent: true },
  { label: 'Software & apps', field: r => r.expenses.software, indent: true },
  { label: 'Marketing fees', field: r => r.expenses.marketingFees, indent: true },
  { label: 'Legal & accounting', field: r => r.expenses.legal, indent: true },
  { label: 'Office & supplies', field: r => r.expenses.office + r.expenses.supplies, indent: true },
  { label: 'Payroll, rent & insurance', field: r => r.expenses.payroll + r.expenses.rent + r.expenses.insurance, indent: true },
  { label: 'Total expenses', field: 'totalExpenses' },
  { label: 'Net operating income', field: 'netOperatingIncome', strong: true },
  { label: 'NOI margin', field: 'noiMargin', ratio: ['netOperatingIncome', 'income'] },
]

const VIEWS = [
  { value: 'plan', label: 'Plan' },
  { value: 'ramped', label: 'Ramped plan' },
  { value: 'reforecast', label: 'Re-forecast' },
  { value: 'variance', label: 'Variance' },
]

const read = (row, field) => (typeof field === 'function' ? field(row) : row[field])

export default function PnlStatement({ plan, reforecast, ramped = null }) {
  const [view, setView] = useState('reforecast')
  const views = ramped ? VIEWS : VIEWS.filter(v => v.value !== 'ramped')
  // The ramped plan stands in for the re-forecast column when chosen, so one
  // renderer serves every view.
  const shown = view === 'ramped' && ramped ? ramped : reforecast

  const sumFields = Object.fromEntries(
    LINES.filter(l => !l.ratio).map(l => [l.label, l.field])
      .concat([['grossProfit', 'grossProfit'], ['netOperatingIncome', 'netOperatingIncome'], ['income', 'income']])
  )
  const planTotals = yearTotals(plan, sumFields)
  const forecastTotals = yearTotals(shown, sumFields)

  /** A cell's value in the chosen view, and whether it is a ratio. */
  const cellValue = (line, planValue, forecastValue) => {
    if (view === 'plan') return planValue
    if (view === 'reforecast' || view === 'ramped') return forecastValue
    // Margins move in points, not percent-of-percent.
    if (line.ratio) return planValue == null || forecastValue == null ? null : forecastValue - planValue
    return variance(forecastValue, planValue)
  }

  const format = (line, value) => {
    if (view === 'variance') {
      if (line.ratio) return value == null ? '—' : `${value > 0 ? '+' : value < 0 ? '−' : ''}${Math.abs(value * 100).toFixed(1)} pts`
      return formatVariance(value)
    }
    return line.ratio ? formatPercent(value) : formatMoney(value)
  }

  const totalRatio = (totals, line) => {
    const [numerator, denominator] = line.ratio
    return totals[denominator] ? totals[numerator] / totals[denominator] : null
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <CardTitle className="text-base">P&L statement</CardTitle>
            <CardDescription className="text-xs">
              Actual months carry actual revenue, refunds, ad spend and COGS. Operating costs are the
              model&apos;s in every month — nothing records them — and an actual month&apos;s COGS is the
              Daily Report&apos;s landed cost, which has no breakdown.
            </CardDescription>
          </div>
          <div className="flex shrink-0 rounded-lg border border-input bg-background p-1">
            {views.map(v => (
              <button
                key={v.value}
                onClick={() => setView(v.value)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  view === v.value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                {v.label}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-xs tabular-nums">
          <thead>
            <tr className="border-b">
              <th className="sticky left-0 bg-card px-2 py-2 text-left font-medium text-muted-foreground" />
              {shown.map(row => (
                <th key={row.month} className="px-2 py-2 text-right font-medium text-muted-foreground whitespace-nowrap">
                  {monthLabel(row.month)}
                  {(view === 'reforecast' || view === 'variance') && row.source !== 'forecast' && (
                    <span className="block text-[10px] font-normal">{row.source === 'paced' ? 'paced' : 'actual'}</span>
                  )}
                </th>
              ))}
              {planTotals.map(total => (
                <th key={total.key} className="px-2 py-2 text-right font-semibold whitespace-nowrap border-l">{total.key}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {LINES.map(line => (
              <tr key={line.label} className={line.strong ? 'border-t bg-muted/30' : ''}>
                <td className={`sticky left-0 bg-card px-2 py-1.5 whitespace-nowrap ${line.strong ? 'font-semibold' : ''} ${line.indent ? 'pl-5 text-muted-foreground' : ''}`}>
                  {line.label}
                </td>
                {shown.map((row, i) => {
                  const value = cellValue(line, read(plan[i], line.field), read(row, line.field))
                  return (
                    <td key={row.month} className={`px-2 py-1.5 text-right whitespace-nowrap ${line.strong ? 'font-semibold' : ''}`}>
                      {format(line, value)}
                    </td>
                  )
                })}
                {planTotals.map((planTotal, t) => {
                  const forecastTotal = forecastTotals[t]
                  const planValue = line.ratio ? totalRatio(planTotal, line) : planTotal[line.label]
                  const forecastValue = line.ratio ? totalRatio(forecastTotal, line) : forecastTotal[line.label]
                  return (
                    <td key={planTotal.key} className="px-2 py-1.5 text-right whitespace-nowrap font-medium border-l">
                      {format(line, cellValue(line, planValue, forecastValue))}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  )
}
